// Password Unlock Modal - Enter password to view protected documents
import React, { useState } from 'react';
import { Lock, Eye, EyeOff, X } from 'lucide-react';

interface PasswordUnlockModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUnlock: (password: string) => Promise<boolean>;
  fileName: string;
}

export default function PasswordUnlockModal({
  isOpen,
  onClose,
  onUnlock,
  fileName
}: PasswordUnlockModalProps) {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isUnlocking, setIsUnlocking] = useState(false);

  if (!isOpen) return null;

  const handleUnlock = async () => {
    if (!password) {
      setError('Please enter password');
      return;
    }

    setIsUnlocking(true);
    setError('');

    try {
      const success = await onUnlock(password);
      if (success) {
        setPassword('');
        setError('');
      } else {
        setError('Incorrect password');
      }
    } catch (err) {
      setError('Incorrect password or corrupted file');
    } finally {
      setIsUnlocking(false);
    }
  };

  const handleClose = () => {
    setPassword('');
    setError('');
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isUnlocking) {
      handleUnlock();
    }
  };

  return (
    <div className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-red-500/30 rounded-2xl p-6 max-w-md w-full shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
              <Lock className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Protected Document</h3>
              <p className="text-xs text-slate-400">Enter password to unlock</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* File Name */}
        <div className="mb-4 p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
          <p className="text-sm text-slate-300 truncate">{fileName}</p>
        </div>

        {/* Password Input */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-slate-300 mb-2">
            Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              onKeyPress={handleKeyPress}
              placeholder="Enter your password"
              disabled={isUnlocking}
              className="w-full px-4 py-3 pr-12 bg-slate-800 border border-slate-600 rounded-lg 
                       text-white placeholder-slate-500 focus:outline-none focus:border-red-500 
                       transition-colors disabled:opacity-50"
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {/* Warning */}
        <div className="mb-6 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <p className="text-xs text-amber-300">
            ⚠️ This document is encrypted. If you've forgotten the password, it cannot be recovered.
          </p>
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleClose}
            disabled={isUnlocking}
            className="flex-1 px-4 py-3 rounded-lg border border-slate-600 hover:bg-slate-800 
                     text-white font-semibold transition-all disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleUnlock}
            disabled={isUnlocking}
            className="flex-1 px-4 py-3 rounded-lg bg-gradient-to-r from-red-500 to-red-600 
                     hover:from-red-600 hover:to-red-700 text-white font-semibold 
                     transition-all shadow-lg shadow-red-500/30 disabled:opacity-50
                     flex items-center justify-center gap-2"
          >
            {isUnlocking ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Unlocking...
              </>
            ) : (
              'Unlock'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
